import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import Header from './components/Header';
import Homepage from './components/Homepage';
import './App.css';
import Applications from './components/Applications';
import Referrals from './components/Referrals';
import Earnings from './components/Earnings';
import Profile from './components/Profile';
import Projects from './components/Projects';
import Interviews from './components/Interviews';
import UrlRouter from './components/UrlRouter';
import './i18n/i18n'; // Initialize i18next
import SignInModal from './components/SignInModal';
import InterviewSignInModal from './components/InterviewSignInModal';
import Placeholder from './components/Placeholder';
import NotFoundHandler from './components/NotFoundHandler';
import { SSIWidget } from './components/SSIWidget';
import { initProgrammaticToken } from './services/apiService';
import { redirectToMobileApp } from './utils/mobileAppRedirect';

const AppContent = () => {
  const { showSignInModal, hideSignIn } = useAuth();
  const location = useLocation();
  const [showInterviewsOverlay, setShowInterviewsOverlay] = useState(false);
  const [showInterviewSignIn, setShowInterviewSignIn] = useState(false);
  const [interviewSignInData, setInterviewSignInData] = useState(null);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // Mobile app redirect - Check if user is on mobile and redirect to app stores
  useEffect(() => {
    // Redirect mobile users to app stores
    const redirected = redirectToMobileApp();
    if (redirected) {
      console.log('[App] Mobile user redirected to app store');
      // Don't initialize token if redirecting
      return;
    }
    
    // Initialize programmatic token once on app load (desktop users only)
    (async () => {
      try {
        const res = await initProgrammaticToken();
        if (!res?.success) {
          console.warn('[App] Programmatic token init failed:', res?.error);
        }
      } catch (e) {
        console.warn('[App] Programmatic token init error:', e);
      }
    })();
  }, []);
  
  // Listen for showInterviewsPage event
  useEffect(() => {
    const handleShowInterviews = (event) => {
      if (event.detail?.preserveUrl) {
        setShowInterviewsOverlay(true);
      }
    };

    const handleShowInterviewSignIn = (event) => {
      const { emailId, mobileNumber, mode } = event.detail || {};
      setInterviewSignInData({ emailId, mobileNumber, mode });
      setShowInterviewSignIn(true);
    };

    window.addEventListener('showInterviewsPage', handleShowInterviews);
    window.addEventListener('showInterviewSignIn', handleShowInterviewSignIn);
    
    return () => {
      window.removeEventListener('showInterviewsPage', handleShowInterviews);
      window.removeEventListener('showInterviewSignIn', handleShowInterviewSignIn);
    };
  }, []);
  
  // Routes that should not show the header
  const routesWithoutHeader = ['/interviews', '/interviewFeedbackScreen'];
  const shouldShowHeader = !routesWithoutHeader.includes(location.pathname) && !showInterviewsOverlay;
  
  // If showing interviews overlay, render interviews component directly
  if (showInterviewsOverlay) {
    return (
      <div>
        <Interviews />
      </div>
    );
  }
  
  return (
    <div>
      {/* Resolve short URLs returned by getActualUrl on startup */}
      <UrlRouter />
      {shouldShowHeader && <Header />}
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/ApplyForJob" element={<Homepage />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/earnings" element={<Earnings />} />
        <Route path="/interviews" element={<Interviews />} />
        <Route path="/interviewFeedbackScreen" element={<Interviews />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/about" element={<Placeholder />} />
        <Route path="/contact" element={<Placeholder />} />
        <Route path="/privacy" element={<Placeholder />} />
        {/* Catch-all route for unknown paths - calls getActualUrl API */}
        <Route path="*" element={<NotFoundHandler />} />
      </Routes>
      
      <SignInModal 
        isOpen={showSignInModal}
        onClose={hideSignIn}
      />
      
      <InterviewSignInModal
        isOpen={showInterviewSignIn}
        onClose={() => {
          setShowInterviewSignIn(false);
          setInterviewSignInData(null);
        }}
        emailId={interviewSignInData?.emailId}
        mobileNumber={interviewSignInData?.mobileNumber}
      />
      
      {/* SSI Widget */}
      <SSIWidget />
    </div>
  );
};

function App() {
  return (
    <Router>
      <ToastProvider>
        <LanguageProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </LanguageProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
